import { ITaskActivitySimple } from "./activity";
import { IAssetResponse } from "./assets";
import { IMerchantSimple } from "./merchant";
import {
  AIAnalysisCache,
  JourneyIntelligence,
  SiteSuitabilityResult,
} from "./state-machine/state-machine";

export enum TaskStatus {
  PENDING = "pending",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELLED = "cancelled",
}

export enum FailureReasonType {
  BOT_DETECTION = "bot_detection",
  /** A login wall stood between the run and the checkout. Structured so the
   *  work pipeline can route it without matching on the error message. */
  LOGIN_REQUIRED = "login_required",
  NO_COUPONS_FOUND = "no_coupons_found",
  COUPON_FETCH_FAILED = "coupon_fetch_failed",
  EXCEEDED_LLM_AGENT_MAX_LIMITS = "exceeded_llm_agent_max_limits",
  BROWSER_LAUNCH_FAILURE = "browser_launch_failure",
  LLM_CONTEXT_LENGTH_EXCEEDED = "llm_context_length_exceeded",
  UNEXPECTED_ERROR = "unexpected_error",
  TEMPLATE_ERRORS = "template_errors",
  // ── Session-replay runs (runType "session") ──────────────────────────────
  NO_CAPTURED_SESSION = "no_captured_session",
  SESSION_TOO_OLD = "session_too_old",
  SESSION_EXPIRED = "session_expired",
  SESSION_CART_EMPTY = "session_cart_empty",
  SESSION_NAVIGATION_FAILED = "session_navigation_failed",
  /** Landed URL did not match the template's checkoutPageUrlRegex — an expired
   * session redirected off checkout, or a stale/incorrect template regex. */
  CHECKOUT_URL_MISMATCH = "checkout_url_mismatch",
  NO_LIVE_TEMPLATE = "no_live_template",
  SKU_TEMPLATE_FAILED = "sku_template_failed",
  // ── Infrastructure / engine failures (not template faults) ────────────────
  /** A data source was unreachable — e.g. Azure SQL firewall / connection failure. */
  DATABASE_ERROR = "database_error",
  /** The bundled headless engine global was missing on the page — injection
   * never took, or the page navigated and wiped it (window.__kindredHeadless). */
  ENGINE_INJECTION_FAILED = "engine_injection_failed",
  /** CDP protocol timeout (Runtime.callFunctionOn) while running the engine. */
  PROTOCOL_TIMEOUT = "protocol_timeout",
  // ── Template capture runs (runType "capture") ────────────────────────────
  CAPTURE_BUDGET_EXCEEDED = "capture_budget_exceeded",
  CAPTURE_LLM_ERRORS_EXCEEDED = "capture_llm_errors_exceeded",
  CAPTURE_ENGINE_TIMEOUT = "capture_engine_timeout",
  /** The site never served its own page (e.g. a Cloudflare 5xx). Distinct from
   *  BOT_DETECTION: no proxy or stealth setting fixes an origin outage. */
  SITE_UNREACHABLE = "site_unreachable",
  /** Every product reached was out of stock. Not a template fault and not
   *  something the run can fix — the merchant needs a purchasable test product. */
  NO_PURCHASABLE_PRODUCT = "no_purchasable_product",
  /** Our proxy refused the request (HTTP 407) or the browser never reached the
   *  site. Ours to fix and retryable — not the merchant's problem. */
  PROXY_UNAVAILABLE = "proxy_unavailable",
  /** The suitability gate classed the site as something we don't test (travel,
   *  insurance, …). A judgement, not a fault, so it never trips the breaker. */
  SITE_NOT_SUITABLE = "site_not_suitable",
  /** Reached the cart but found no coupon field there and no way on to checkout. */
  COUPON_FORM_NOT_FOUND = "coupon_form_not_found",
  /** Had a basket but every route onward led somewhere that was not a checkout. */
  CHECKOUT_NOT_REACHED = "checkout_not_reached",
}

/** Why a run exists. How it is driven is `executionMode`. */
export type TRunType = "live" | "audit" | "capture";

/**
 * How a run was driven. Written by initialisation once it knows what the
 * merchant has; null until then. A caller may set it up front to force a path.
 */
export type TExecutionMode = "replay" | "api" | "walk";

/** Device the run emulates. Mobile is used by the unified merchant-map capture. */
export type TDevice = "mobile" | "desktop";

/**
 * Why a capture run is happening. Both purposes walk the same journey; they
 * differ in what they are allowed to own.
 *
 * "template" — the scheduled validation of a domain's journey. Writes the
 *   merchant map and stamps its freshness. Runs for ONE geo per domain (the
 *   primary), because a template is page structure and structure belongs to the
 *   site, not the market.
 *
 * "session" — a merchant needs a replayable session or API credentials before it
 *   can be coupon-tested cheaply. Runs for that specific geo, through that geo's
 *   proxy, and deliberately does NOT write the template: a walk done under one
 *   market can carry that market's locale into a URL pattern the other markets
 *   read.
 */
export type TCapturePurpose = "template" | "session";

export interface ITaskSimple {
  _id: string;
  merchantId?: string;
  checkoutType?: string;
  status: TaskStatus;
  domain: string;
  countryCode: string;
  startTime: Date;
  lastActivity: Date;
  llmTotalCost: number;
  visionCallCount?: number;
  visionTotalCost?: number;
  runType?: TRunType;
  capturePurpose?: TCapturePurpose;
  executionMode?: TExecutionMode;
  device?: TDevice;

  ranSuccessfully?: boolean;
  errorMessage?: string;
  errorType?: FailureReasonType;
  /** Where in the funnel the run stopped. Derived, never parsed from prose. */
  failureStage?: FailureStage;
  /** Who has to act on it. */
  failureOwner?: FailureOwner;
  /** What a person could do about it, if anything. */
  helpKind?: HelpKind | null;
  /** Deepest `AutomationState` the run reached — the evidence behind the stage. */
  deepestState?: string;
  /**
   * The task this one was queued to retry, on a different execution path.
   *
   * Set only on API-failure fallbacks. It is also the loop guard: a task that
   * carries it can never spawn a fallback of its own, so one API failure can
   * produce at most one retry.
   */
  fallbackFromTaskId?: string | null;
  requiredHumanIntervention?: boolean;
  notSuitable?: boolean;
  siteSuitability?: SiteSuitabilityResult;
}

export interface IStartTask {
  merchantId: string;
  domain: string;
  countryCode: string;
  runType?: TRunType;
  capturePurpose?: TCapturePurpose;
  executionMode?: TExecutionMode;
  device?: TDevice;
  /** Set only by the API-failure fallback; also its loop guard. */
  fallbackFromTaskId?: string | null;
}

export interface ITaskWithMerchant extends ITaskSimple {
  merchant: IMerchantSimple;
  couponsToTest: string[];
  result?: ITestResult;
}

export interface ITaskWithActivity extends ITaskWithMerchant {
  activities: ITaskActivitySimple[];
}

export enum activityType {
  "page_evaluation" = "page_evaluation",
  "page_navigation" = "page_navigation",
  "page_interaction" = "page_interaction",
  "task_failure" = "task_failure",
  "task_success" = "task_success",
  "human_interaction_request" = "human_interaction_request",
  "human_interaction_response" = "human_interaction_response",
  "ai_agent_interaction" = "ai_agent_interaction",
}

export enum handlerType {
  "human" = "human",
  "system" = "system",
  "ai_agent" = "ai_agent",
}

export interface ICouponResult {
  code: string;
  applied: boolean;
  savings: number;
  percentageSavings?: number;
  notApplicableToBasket?: boolean;
  systemError?: boolean;
  errorMessage?: string;
  screenshot?: IAssetResponse;
  publishedStatus?: number | null;
  source?: "api" | "db" | null;
}
export interface ITestResult {
  startedAt: Date;
  finishedAt: Date;
  originalTotal: number;
  currency: string;
  bestCoupon: string;
  selectors: {
    [key: string]: string;
  };
  appliedCoupons: ICouponResult[];
}

export interface IUpsertTask {
  journeyIntelligence: JourneyIntelligence;
  aiAnalysisCache: AIAnalysisCache;
  result: ITestResult;

  ranSuccessfully?: boolean;
  errorMessage?: string;
  requiredHumanIntervention?: boolean;
  notSuitable?: boolean;
  siteSuitability?: SiteSuitabilityResult;
}

/**
 * WHAT kind of help a failed run needs — the routing key for the template
 * pipeline. Derived from the reason and the stage, never from message text.
 */
export enum HelpKind {
  /** A person signs in with Kindred's test account and captures the session. */
  SIGN_IN = "sign-in",
  /** A challenge page; a session captured past it may get through. */
  BOT_WALL = "bot-wall",
  /** A person shows the walk the way: product, basket, checkout, coupon field. */
  TEACH_JOURNEY = "teach-journey",
  /** Classed as a site we don't test. Someone decides whether to run it anyway. */
  NOT_SUITABLE = "not-suitable",
  /** Every product was out of stock; the merchant needs a test product. */
  TEST_PRODUCT = "test-product",
  /** The stored template or route broke — developer work. */
  TEMPLATE = "template",
  /** Ours: proxy, crash, timeout, LLM limits. Retried, not shown to testers. */
  INFRA = "infra",
  /** Nothing to test or upstream data; not a site problem at all. */
  DATA = "data",
}

/**
 * WHERE in the funnel a run stopped.
 *
 * Deliberately separate from `FailureReasonType`, which says WHAT kind of error
 * occurred and is 96% one value (`no_coupons_found`) — useless for asking "how
 * far did we get". A stage is derived from the deepest state the machine
 * actually reached, not from parsing the error message.
 */
export enum FailureStage {
  /** Never started — no codes to test, cancelled, or claimed and dropped. */
  NOT_STARTED = "not_started",
  /** Could not load or reach the site at all. */
  REACH_SITE = "reach_site",
  /** Reached the site, could not find a testable product. */
  PRODUCT_DISCOVERY = "product_discovery",
  /** Found a product, could not get it into a basket. */
  ADD_TO_CART = "add_to_cart",
  /** Basket exists, never arrived at a checkout page. */
  REACH_CHECKOUT = "reach_checkout",
  /** At checkout, could not find or open the coupon form. */
  COUPON_FORM = "coupon_form",
  /** Entered a code; could not read what the site did with it. */
  APPLY = "apply",
  /** Got a decisive answer — this is not a failure stage. */
  COMPLETED = "completed",
}

/**
 * WHO has to act. The point of splitting this out is that a bot wall is not the
 * coupon's fault and must not consume the coupon's retry budget, and a template
 * fault is not a site fault.
 */
export enum FailureOwner {
  /** The merchant's site: bot wall, login wall, outage, no stock. */
  SITE = "site",
  /** Our stored route/template: stale selector, wrong cart URL, bad regex. */
  ROUTE = "route",
  /** Our code or infrastructure: crash, timeout, injection failure. */
  ENGINE = "engine",
  /** Upstream data: no codes, wrong geo, unreachable database. */
  DATA = "data",
}
